import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Clock, ChevronDown, Check } from 'lucide-react';
import { apiCall } from '@/api/apiCall';
import { AreaChartShimmer } from '@/components/child/SectionShimmer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AreaChart from '@/components/child/AreaChart';

const VIEW_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'top_5',   label: 'Top 5'    },
  { value: 'top_10',  label: 'Top 10'   },
];

const PAY_STATUS_OPTIONS = [
  { value: 'ALL',        label: 'ALL'         },
  { value: 'PAID',       label: 'PAID'        },
  { value: 'FREE',       label: 'MEMBER'      }, // display "Member", send "FREE"
  { value: 'RENT A CAR', label: 'RENT A CAR'  },
];

// 👇 NEW: sirf Device-level panel ke liye — device ki apni gate-scoped location
// ("{location} - {gate}") vs poori "All_Locations" scope ke beech toggle karta
// hai. "General" (Unknown) gate wale devices mein ye dropdown dikhta hi nahi.
const LOCATION_SCOPE_OPTIONS = [
  { value: 'gate', label: 'Gate Level'   },
  { value: 'all',  label: 'All Locations' },
];

// 👇 Hourly chart ka apna measure dropdown (filter ki currency se independent)
// Full list — offstreet ke liye sab options
const MEASURE_OPTIONS_ALL = [
  { value: 'Ticket_Count',     label: 'Ticket Count'  },
  { value: 'Amount_Collected', label: 'Revenue in LC' },
  { value: 'Amount in RC',     label: 'Revenue in RC' },
];

const HourlyRevenueChart = ({
  deviceId,
  // 👇 NEW: sirf Device-level panel se pass hota hai. Agar ye diya gaya hai to
  // hum "hourly-graph-by-device" endpoint use karte hain — usmein "location"
  // (yaani upar wala `deviceId` prop, jo "{location} - {gate}" format mein
  // hota hai, e.g. "LMP - PA1") AND "device_id" (yeh `apiDeviceId`, e.g.
  // "9663033") dono alag-alag query params ke tor par jaate hain.
  // Baaki saare (Region/Country/Ops/Location/Gate) levels par ye prop nahi
  // diya jaata — unka behavior bilkul pehle jaisa hi rehta hai.
  apiDeviceId,
  // 👇 NEW: sirf Device-level panel se pass hota hai. Batata hai ke is device
  // ka koi real gate hai ya nahi ("General"/Unknown gate wale devices ke liye
  // false) — isi se decide hota hai ke Gate Level / All Locations dropdown
  // dikhega ya nahi.
  hasGate = false,
  month,
  year,
  title = "Hourly Revenue Distribution",
  className = "",
  valueType = "",
  currency = "local", // ab sirf legacy prop hai, hourly fetch mein use nahi hoga
}) => {
  const [hourlyData,      setHourlyData]      = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [totalCollection, setTotalCollection] = useState(0);
  const [viewType,        setViewType]        = useState('all_time');
  const [payStatus,       setPayStatus]       = useState('ALL');
  const [measureType,     setMeasureType]     = useState('Ticket_Count'); // 👈 default
  // 👇 NEW: sirf Device-level (apiDeviceId + hasGate) ke liye — 'gate' (default)
  // ya 'all' (All_Locations scope)
  const [locationScope,   setLocationScope]   = useState('gate');

  // ── Small card ke dropdowns ──
  const [viewDropdown,    setViewDropdown]    = useState(false);
  const [payDropdown,     setPayDropdown]     = useState(false);
  const [measureDropdown, setMeasureDropdown] = useState(false);
  const [locationScopeDropdown, setLocationScopeDropdown] = useState(false);
  const viewDDRef    = useRef(null);
  const payDDRef     = useRef(null);
  const measureDDRef = useRef(null);
  const locationScopeDDRef = useRef(null);


  // ── ✅ Popup — apna heading + filters khud dikhata hai ──
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalViewDropdown,    setModalViewDropdown]    = useState(false);
  const [modalPayDropdown,     setModalPayDropdown]     = useState(false);
  const [modalMeasureDropdown, setModalMeasureDropdown] = useState(false);
  const [modalLocationScopeDropdown, setModalLocationScopeDropdown] = useState(false);
  const modalViewDDRef    = useRef(null);
  const modalPayDDRef     = useRef(null);
  const modalMeasureDDRef = useRef(null);
  const modalLocationScopeDDRef = useRef(null);
const hasFetchedRef = useRef(false);
  const wrapperRef = useRef(null);

  // Ye dropdown sirf Device-level panel mein, aur sirf real-gate devices ke
  // liye dikhta hai — "General" (Unknown gate) devices mein bilkul nahi.
  const showLocationScopeDropdown = !!apiDeviceId && !!hasGate;

  // ✅ NEW: On-street (ya koi bhi non-offstreet) ke liye "Revenue in RC" hide karo
  const MEASURE_OPTIONS = valueType === 'offstreet'
    ? MEASURE_OPTIONS_ALL
    : MEASURE_OPTIONS_ALL.filter(o => o.value !== 'Amount in RC');

  // ✅ NEW: Agar kisi wajah se RC select hai aur ab non-offstreet ho gaya, reset karo
  useEffect(() => {
    if (valueType !== 'offstreet' && measureType === 'Amount in RC') {
      setMeasureType('Ticket_Count');
    }
  }, [valueType, measureType]);

  // Close dropdowns on outside click — small card + popup, dono ke dropdowns handle karta hai
  useEffect(() => {
    const handler = (e) => {
      if (viewDDRef.current    && !viewDDRef.current.contains(e.target))    setViewDropdown(false);
      if (payDDRef.current     && !payDDRef.current.contains(e.target))     setPayDropdown(false);
      if (measureDDRef.current && !measureDDRef.current.contains(e.target)) setMeasureDropdown(false);
      if (locationScopeDDRef.current && !locationScopeDDRef.current.contains(e.target)) setLocationScopeDropdown(false);

      if (modalViewDDRef.current    && !modalViewDDRef.current.contains(e.target))    setModalViewDropdown(false);
      if (modalPayDDRef.current     && !modalPayDDRef.current.contains(e.target))     setModalPayDropdown(false);
      if (modalMeasureDDRef.current && !modalMeasureDDRef.current.contains(e.target)) setModalMeasureDropdown(false);
      if (modalLocationScopeDDRef.current && !modalLocationScopeDDRef.current.contains(e.target)) setModalLocationScopeDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchHourlyData = useCallback(async () => {
    if (!deviceId || !month || !year) return;
    // Device-level (apiDeviceId) ko device_id bhi chahiye — uske bina fetch mat karo
    if (apiDeviceId !== undefined && apiDeviceId !== null && !apiDeviceId) return;
     if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // measure ab dropdown se aayega, currency filter se nahi
      const measure = measureType;

      // pay_status param — "ALL" hone pe param mat bhejo
      const payParam = payStatus && payStatus !== 'ALL'
        ? `&pay_status=${encodeURIComponent(payStatus)}`
        : '';

      // 👇 Gate Level / All Locations dropdown (sirf real-gate device-level charts
      // mein dikhta hai) — "all" select hone par location "All_Locations" ban
      // jaati hai, warna hamesha wahi merged "{location} - {gate}" (deviceId prop).
      // "General" (Unknown gate, hasGate=false) devices ke liye dropdown hai hi
      // nahi — unke liye hamesha default "All_Locations" jaata hai.
      const effectiveLocation = apiDeviceId && !hasGate
        ? 'All_Locations'
        : (showLocationScopeDropdown && locationScope === 'all')
          ? 'All_Locations'
          : deviceId;

      // 👇 Device-level: "location" (effectiveLocation) + "device_id" (apiDeviceId)
      // dono alag params ke tor par jaate hain — ye ek dedicated endpoint hai.
      const url = apiDeviceId
        ? `/off-street-map/hourly-graph-by-device?location=${encodeURIComponent(effectiveLocation)}&device_id=${encodeURIComponent(apiDeviceId)}&year=${year}&month=${month}&view_type=${viewType}&measure=${encodeURIComponent(measure)}${payParam}`
        : valueType === 'offstreet'
        ? `/off-street-map/hourly-graph?location=${encodeURIComponent(deviceId)}&year=${year}&month=${month}&view_type=${viewType}&measure=${encodeURIComponent(measure)}${payParam}`
        : `/get-hourly-duration-pnds?device_id=${encodeURIComponent(deviceId)}&month=${month}&year=${year}&view_type=${viewType}${valueType ? `&value_type=${valueType}` : ''}&measure=${encodeURIComponent(measure)}${payParam}`;

      const response = await apiCall('get', url, null, null, false, false);

      if (response?.success && response?.hourly) {
        // ✅ API hamesha "collection" field mein value deta hai,
        // chahe measure Ticket_Count ho ya Amount_Collected/Amount in RC
        // (device-level endpoint "ticket_count" field bhejta hai — dono handle karte hain)
        const chartData = response.hourly.map(item => ({
          time:  item.hour,
          value: parseFloat(
            (item.collection ?? item.ticket_count ?? '0').toString().replace(/,/g, '')
          ) || 0,
        }));
        setHourlyData(chartData);
        setTotalCollection(chartData.reduce((sum, item) => sum + item.value, 0));
        setError(null);
      } else {
        setError(response?.message || "No hourly data available for this period");
        setHourlyData([]);
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : "Failed to fetch hourly revenue data");
      setHourlyData([]);
    } finally {
      setLoading(false);
      hasFetchedRef.current = false;
    }
  }, [deviceId, apiDeviceId, hasGate, month, year, viewType, payStatus, measureType, valueType, locationScope, showLocationScopeDropdown]);

  useEffect(() => { fetchHourlyData(); }, [fetchHourlyData]);

  const selectedView      = VIEW_OPTIONS.find(o => o.value === viewType)       || VIEW_OPTIONS[0];
  const selectedPayStatus = PAY_STATUS_OPTIONS.find(o => o.value === payStatus) || PAY_STATUS_OPTIONS[0];
  const selectedMeasure   = MEASURE_OPTIONS.find(o => o.value === measureType)  || MEASURE_OPTIONS[0];
  const selectedLocationScope = LOCATION_SCOPE_OPTIONS.find(o => o.value === locationScope) || LOCATION_SCOPE_OPTIONS[0];
  const measureLabel      = selectedMeasure.label; // "Ticket Count" / "Revenue in LC" / "Revenue in RC"

  // ── Reusable mini dropdown ──────────────────────────────────────────────────
  const MiniDropdown = ({ ddRef, open, setOpen, selected, options, onChange }) => (
    <div ref={ddRef} className="relative flex-shrink-0">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-colors"
        style={{
          background: 'rgba(255,255,255,0.12)',
          color: '#ffffff',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        {selected.label}
        <ChevronDown
          className="w-3 h-3 transition-transform duration-150"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 rounded-lg overflow-hidden z-50"
          style={{
            background: '#2a2a2b',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            minWidth: '120px',
          }}
        >
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={(e) => { e.stopPropagation(); onChange(opt.value); setOpen(false); }}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[10px] font-medium transition-colors text-left"
              style={{
                color:      selected.value === opt.value ? '#10B981' : '#ffffff',
                background: selected.value === opt.value ? 'rgba(16,185,129,0.12)' : 'transparent',
              }}
              onMouseEnter={e => { if (selected.value !== opt.value) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={e => { if (selected.value !== opt.value) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt.label}
              {selected.value === opt.value && <Check className="w-2.5 h-2.5 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ── Shared header bar (small card ke liye) ───────────────────────────────────
  const HeaderBar = () => (
    <div className="flex items-center justify-between px-3 pt-2 pb-1 gap-2">
      <div className="text-left min-w-0 flex-1">
        <p className="text-xs font-medium truncate" style={{ color: '#ffffff' }}>{title}</p>
        <p className="text-[10px] leading-tight" style={{ color: '#ffffff99' }}>
          Average Daily Transaction Time Graph
        </p>
      </div>

      {/* Gate Level / All Locations dropdown — sirf Device-level, real-gate devices ke liye */}
      {showLocationScopeDropdown && (
        <MiniDropdown
          ddRef={locationScopeDDRef}
          open={locationScopeDropdown}
          setOpen={setLocationScopeDropdown}
          selected={selectedLocationScope}
          options={LOCATION_SCOPE_OPTIONS}
          onChange={setLocationScope}
        />
      )}

      {/* Measure dropdown — Ticket Count / Revenue in LC / (Revenue in RC sirf offstreet) */}
      <MiniDropdown
        ddRef={measureDDRef}
        open={measureDropdown}
        setOpen={setMeasureDropdown}
        selected={selectedMeasure}
        options={MEASURE_OPTIONS}
        onChange={setMeasureType}
      />

      {/* Pay Status dropdown — sirf offstreet ke liye dikhao, Device-level (apiDeviceId) ke liye nahi */}
      {valueType === 'offstreet' && !apiDeviceId && (
        <MiniDropdown
          ddRef={payDDRef}
          open={payDropdown}
          setOpen={setPayDropdown}
          selected={selectedPayStatus}
          options={PAY_STATUS_OPTIONS}
          onChange={setPayStatus}
        />
      )}

      {/* View Type dropdown */}
      <MiniDropdown
        ddRef={viewDDRef}
        open={viewDropdown}
        setOpen={setViewDropdown}
        selected={selectedView}
        options={VIEW_OPTIONS}
        onChange={setViewType}
      />
    </div>
  );

  // ── ✅ Popup ke andar wala header bar — same design, alag open/close state
  // (taake small card ke dropdown se conflict na ho jab dono ek sath mounted hon) ──
  const ModalHeaderBar = () => (
    <div className="flex items-center justify-between px-1 pb-3 gap-2 flex-wrap">
      <div className="text-left min-w-0 flex-1">
        <p className="text-sm font-semibold" style={{ color: '#ffffff' }}>{title}</p>
        <p className="text-xs leading-tight" style={{ color: '#ffffff99' }}>
          Average Daily Transaction Time Graph
        </p>
      </div>

      {showLocationScopeDropdown && (
        <MiniDropdown
          ddRef={modalLocationScopeDDRef}
          open={modalLocationScopeDropdown}
          setOpen={setModalLocationScopeDropdown}
          selected={selectedLocationScope}
          options={LOCATION_SCOPE_OPTIONS}
          onChange={setLocationScope}
        />
      )}

      <MiniDropdown
        ddRef={modalMeasureDDRef}
        open={modalMeasureDropdown}
        setOpen={setModalMeasureDropdown}
        selected={selectedMeasure}
        options={MEASURE_OPTIONS}
        onChange={setMeasureType}
      />

      {valueType === 'offstreet' && !apiDeviceId && (
        <MiniDropdown
          ddRef={modalPayDDRef}
          open={modalPayDropdown}
          setOpen={setModalPayDropdown}
          selected={selectedPayStatus}
          options={PAY_STATUS_OPTIONS}
          onChange={setPayStatus}
        />
      )}

      <MiniDropdown
        ddRef={modalViewDDRef}
        open={modalViewDropdown}
        setOpen={setModalViewDropdown}
        selected={selectedView}
        options={VIEW_OPTIONS}
        onChange={setViewType}
      />
    </div>
  );

  // ── Small card ke andar (chart area) ka content — loading/error/empty/chart ──
  const renderSmallCardBody = () => {
    if (loading) {
      return <AreaChartShimmer columns={24} height={260} />;
    }
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-8">
          <Clock className="w-8 h-8" style={{ color: '#ffffff40' }} />
          <p className="text-sm text-red-400 text-center">{error}</p>
          <p className="text-xs" style={{ color: '#ffffff60' }}>For {month} {year}</p>
        </div>
      );
    }
    if (!hourlyData.length) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-8">
          <Clock className="w-8 h-8" style={{ color: '#ffffff40' }} />
          <p className="text-sm text-center" style={{ color: '#ffffff80' }}>No hourly revenue data available</p>
          <p className="text-xs" style={{ color: '#ffffff60' }}>For {month} {year}</p>
        </div>
      );
    }
    return (
      /*
        ✅ Double-click yahin se detect hoga — poore chart area (canvas + table) pe.
      */
      <div
        onDoubleClick={() => setIsModalOpen(true)}
        style={{ cursor: 'zoom-in' }}
      >
        <AreaChart
          data={hourlyData}
          yAxisLabel={measureLabel}
          tableRowLabel={measureLabel}
          areaStartColor="#10B981"
          areaEndColor="#10B981"
          headerBadge=""
          tableHeaderFontSize="6px"
          tableValueFontSize="6px"
          tableLabelFontSize="6px"
          firstColumnLabel="Time"
        />
      </div>
    );
  };

  // ── Popup ke andar ka content — loading/error/empty/chart ──
  const renderModalBody = () => {
    if (loading) {
      return <AreaChartShimmer columns={24} height={480} />;
    }
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Clock className="w-8 h-8" style={{ color: '#ffffff40' }} />
          <p className="text-sm text-red-400 text-center">{error}</p>
        </div>
      );
    }
    if (!hourlyData.length) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Clock className="w-8 h-8" style={{ color: '#ffffff40' }} />
          <p className="text-sm text-center" style={{ color: '#ffffff80' }}>No hourly revenue data available</p>
        </div>
      );
    }
    return (
      <AreaChart
        data={hourlyData}
        yAxisLabel={measureLabel}
        tableRowLabel={measureLabel}
        areaStartColor="#10B981"
        areaEndColor="#10B981"
        headerBadge=""
        tableHeaderFontSize="9px"
        tableValueFontSize="9px"
        tableLabelFontSize="9px"
        chartHeight={480}
        firstColumnLabel="Time"
      />
    );
  };

  return (
    <div ref={wrapperRef} style={{ backgroundColor: '#464647' }} className={`rounded-xl overflow-hidden ${className}`}>
      <HeaderBar />
      {renderSmallCardBody()}

      {/* ═══════════════════════════════════════════════════════════════════
          ✅ Popup — hamesha mounted rehta hai (sirf isModalOpen se dikhta/chupta
          hai). Isliye filter change > loading > naya data aane pe ye band-khul
          nahi hota — bas andar ka content update hota hai.

          ✅ FIX: popup ki width badha di (98vw / up to 2200px on big screens)
          taake 24 hourly columns wali table ki values overlap na karein.
         ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
  <DialogContent className="w-[98vw] sm:max-w-[98vw] lg:max-w-[1900px] xl:max-w-[1600px] max-h-[92vh] [&>button]:opacity-100 [&>button]:bg-zinc-800 [&>button]:text-white [&>button]:hover:bg-zinc-700 [&>button]:hover:text-white [&>button]:rounded-full [&>button]:p-1.5 [&>button]:shadow-md">
    <DialogHeader className="sr-only">
      <DialogTitle>{title}</DialogTitle>
    </DialogHeader>

    <div
      style={{
        backgroundColor: '#464647',
        borderRadius: '10px',
        padding: '12px',
        marginTop: '26px',
        maxHeight: 'calc(92vh - 76px)',
        overflowY: 'auto',
      }}
    >
      <ModalHeaderBar />
      {renderModalBody()}
    </div>
  </DialogContent>
</Dialog>
    </div>
  );
};

export default HourlyRevenueChart;